/* When the ring is not the whole book, and what to say about it.
 *
 * The donut is one arc per slice sized by weight (§7.1), and an arc has no
 * negative length. So a part of the book worth less than nothing cannot be
 * drawn, and the screen dropped it: the cash slice is built only when the cash
 * is positive.
 *
 * Dropping it is right. Saying nothing about it was not. The amount stayed in
 * the book's total, so the ring added up to more than the figure above it, and
 * because the ring divides each arc by the total of the arcs it drew rather
 * than by the book, it still closed into a full circle. Nothing on the screen
 * suggested a slice was missing. Measured on a test book: ring $60,078, book
 * $59,646, percentages summing to 100.6, and no cash slice anywhere.
 *
 * A balance below zero is not an error state to be cleaned up — the book treats
 * it as the most useful thing it can tell you, a spend it saw without the
 * receipt that paid for it. It is going to happen, and when it does the ring
 * has to account for itself.
 *
 * So the ring goes on showing what it can draw, which is a true picture of what
 * is held, and a line underneath names the difference. Both totals, and the
 * reason, in a sentence. It says nothing about what to do (§1).
 *
 * The same line covers the other way this can happen — anything in the total
 * that no slice carries — rather than testing for cash specifically. What
 * matters to the reader is that the ring is not the whole book and by how much,
 * and that is the same fact whichever part is missing. */

/** Below this the difference is rounding, not a missing slice. Half a dollar,
    because slice values are money and the screen rounds them to whole dollars
    anyway. */
const NOISE_USD = 0.5;

export interface RingGap {
  /** What the arcs come to. */
  ringUsd: number;
  /** What the book comes to. */
  bookUsd: number;
  /** book minus ring. Negative when the ring shows more than the book is
      worth, which is what a balance below zero does. */
  differenceUsd: number;
}

/** The gap between what the ring draws and what the book is worth, or null when
    they agree — which is the usual case, and then the screen says nothing. */
export function ringGap(
  drawn: Array<{ weight: number; value: number }>,
  bookUsd: number,
): RingGap | null {
  /* Only the arcs that are actually drawn. The ring leaves out anything without
     a positive weight, so counting those here would report a ring that agrees
     with the book while the picture on screen does not. */
  const ringUsd = drawn
    .filter((s) => s.weight > 0)
    .reduce((sum, s) => sum + s.value, 0);

  const differenceUsd = bookUsd - ringUsd;
  if (Math.abs(differenceUsd) < NOISE_USD) return null;
  return { ringUsd, bookUsd, differenceUsd };
}
